import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Pencil, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel, HEALTH_LABELS,
} from "@/components/app/primitives";
import { moneyCompact, pct, relativeTime, shortDay, dueLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STAGES = ["Discovery", "Build", "Launch", "Scale"];
const HEALTHS = ["on_track", "at_risk", "blocked"];

function KpiBlock({ label, value, sub }) {
  return (
    <div className="flex flex-col gap-1.5 px-4 py-3 min-w-[140px] flex-1">
      <SectionLabel>{label}</SectionLabel>
      <span className="font-mono tabular-nums text-[18px] leading-none text-foreground">{value}</span>
      {sub}
    </div>
  );
}

function CapitalStrip({ strip }) {
  if (!strip) return null;
  if (strip.restricted) {
    return (
      <div data-testid="capital-strip" className="rounded-md border border-border bg-card px-4 py-4 flex items-center gap-3">
        <Lock className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
        <span className="text-sm text-muted-foreground">Capital position is restricted to the studio team.</span>
      </div>
    );
  }
  const committedPct = strip.target ? (strip.committed / strip.target) * 100 : 0;
  const cur = strip.currency || "SGD";
  return (
    <div data-testid="capital-strip" className="rounded-md border border-border bg-card shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <div className="flex flex-wrap divide-x divide-border">
        <KpiBlock
          label="Raise, committed of target"
          value={`${moneyCompact(strip.committed, cur)} / ${moneyCompact(strip.target, cur)}`}
          sub={
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-28 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${Math.min(committedPct, 100)}%` }} data-testid="capital-strip-progress" />
              </div>
              <span className="font-mono tabular-nums text-[11px] text-muted-foreground">{pct(committedPct, 0)}</span>
            </div>
          }
        />
        <KpiBlock label="Cash on hand" value={<span data-testid="capital-strip-cash">{moneyCompact(strip.cash_on_hand, cur)}</span>} />
        <KpiBlock label="Monthly burn" value={moneyCompact(strip.monthly_burn, cur)} />
        <KpiBlock
          label="Blended runway"
          value={<span data-testid="capital-strip-runway">{strip.runway_months != null ? `${strip.runway_months} mo` : "\u00B7"}</span>}
        />
        <KpiBlock
          label="Updated"
          value={<span className="text-[14px]">{relativeTime(strip.updated_at)}</span>}
          sub={strip.is_placeholder ? <span className="text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">Placeholder</span> : null}
        />
      </div>
    </div>
  );
}

function VentureEditDialog({ venture, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    health: venture.health,
    stage: venture.stage,
    headline_metric_value: venture.headline_metric_value || "",
    next_milestone: venture.next_milestone || "",
    next_milestone_due: venture.next_milestone_due ? venture.next_milestone_due.slice(0, 10) : "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/ventures/${venture.id}`, form);
      toast.success(`${venture.name} updated`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update venture");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`venture-edit-${venture.id}`} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Update
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Update {venture.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Health</Label>
              <Select value={form.health} onValueChange={(v) => set("health", v)}>
                <SelectTrigger data-testid="venture-health-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HEALTHS.map((h) => <SelectItem key={h} value={h}>{HEALTH_LABELS[h]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Headline metric</Label>
            <Input value={form.headline_metric_value} onChange={(e) => set("headline_metric_value", e.target.value)} className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Next milestone</Label>
            <Input value={form.next_milestone} onChange={(e) => set("next_milestone", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Milestone due</Label>
            <Input type="date" value={form.next_milestone_due} onChange={(e) => set("next_milestone_due", e.target.value)} className="font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="venture-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">
            {saving ? "Saving" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VentureCard({ venture, onSaved }) {
  return (
    <div data-testid="venture-health-card" className="rounded-md border border-border bg-card shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <div className="flex items-start justify-between gap-3 p-4">
        <div>
          <h3 className="font-display text-xl text-foreground leading-none">{venture.name}</h3>
          <p className="mt-1.5 text-xs text-muted-foreground">{venture.arm} <span className="text-muted-foreground/50">/</span> {venture.stage}</p>
        </div>
        <StatusPill tone={venture.health} label={HEALTH_LABELS[venture.health] || venture.health} testId="venture-health-card-health-pill" />
      </div>
      <div className="px-4 pb-4">
        <div className="rounded-md border border-border bg-background/40 px-3 py-3">
          <SectionLabel>{venture.headline_metric_label || "Headline metric"}</SectionLabel>
          <p data-testid="venture-headline-metric" className="mt-1.5 font-mono tabular-nums text-2xl text-foreground leading-none">
            {venture.headline_metric_value || "\u00B7"}
          </p>
        </div>
        <div className="mt-3">
          <SectionLabel>Next milestone</SectionLabel>
          <p className="mt-1 text-sm text-foreground">{venture.next_milestone || "Not set"}</p>
          {venture.next_milestone_due && (
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">Due {shortDay(venture.next_milestone_due)}</p>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">Owner: <span className="text-foreground">{venture.lead_name || "Unassigned"}</span></span>
          {venture.can_edit && <VentureEditDialog venture={venture} onSaved={onSaved} />}
        </div>
      </div>
    </div>
  );
}

function ThisWeek({ items }) {
  return (
    <Panel title="This week" testId="this-week-panel" className="h-full">
      {items.length === 0 ? (
        <EmptyState message="No priorities due this week." testId="this-week-empty" />
      ) : (
        <ul className="divide-y divide-border">
          {items.map((t) => {
            const overdue = dueLabel(t.due).includes("overdue");
            const today = dueLabel(t.due) === "Today";
            return (
              <li key={t.id} data-testid="this-week-item" className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5">
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t.venture_name ? `${t.venture_name} / ` : ""}{t.arm}
                  </p>
                </div>
                <StatusPill
                  dot={false}
                  tone={overdue ? "blocked" : today ? "accent" : "neutral"}
                  label={dueLabel(t.due)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function ActivityFeed({ items }) {
  return (
    <Panel title="Activity" testId="activity-feed">
      {items.length === 0 ? (
        <EmptyState message="No activity recorded in the last 24 hours." />
      ) : (
        <ul className="divide-y divide-border">
          {items.map((a) => (
            <li key={a.id} data-testid="activity-feed-item" className="flex items-start gap-3 px-4 py-3">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  <span className="capitalize">{a.action}</span>: <span className="text-muted-foreground">{a.target}</span>
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground font-mono">{a.actor} <span className="text-muted-foreground/50">·</span> {relativeTime(a.timestamp)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function Cockpit() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/cockpit");
      setData(res.data);
    } catch (e) {
      toast.error("Could not load the cockpit.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading label="Loading the cockpit" />;
  if (!data) return <EmptyState message="Nothing to show yet." />;

  return (
    <div className="space-y-5">
      <CapitalStrip strip={data.capital_strip} />

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <SectionLabel>Active ventures</SectionLabel>
            <span className="font-mono text-[11px] text-muted-foreground">{data.ventures.length} active</span>
          </div>
          {data.ventures.length === 0 ? (
            <EmptyState message="No ventures are visible to you." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.ventures.map((v) => <VentureCard key={v.id} venture={v} onSaved={load} />)}
            </div>
          )}
        </div>
        <div className="col-span-12 xl:col-span-5">
          <ThisWeek items={data.this_week} />
        </div>
      </div>

      <ActivityFeed items={data.activity} />
    </div>
  );
}
